import {PriceRequest} from '../services/api/price'

const getVolumePrice = (priceRequest: Partial<PriceRequest>, volume: number) => {
  if (typeof priceRequest.volumePrice === 'number') {
    return priceRequest.volumePrice as number
  }

  if (
    typeof priceRequest.volumePrice === 'object' &&
    priceRequest.volumePrice &&
    priceRequest.volumePrice.length > 0
  ) {
    let item = priceRequest.volumePrice[0]
    for (let i = 0; i < priceRequest.volumePrice.length; i += 1) {
      if (volume < priceRequest.volumePrice[i].volume) {
        break
      }
      item = priceRequest.volumePrice[i]
    }

    return item.price
  }

  return 0
}

const roundNumber = (value: number) => Math.round(value * 100) / 100

type ModelProperties = {
  volume?: number
  boundingBox?: number
  surfaceArea?: number
}

export const calculateModelGeometryPrice = (
  priceRequest: Partial<PriceRequest>,
  {volume = 0, boundingBox = 0, surfaceArea = 0}: ModelProperties
) => {
  if (!priceRequest.volumePrice && !priceRequest.weightPrice) {
    return 0
  }

  const volumePrice = priceRequest.weightPrice
    ? volume * priceRequest.weightPrice * (priceRequest.density || 0)
    : volume * getVolumePrice(priceRequest, volume)

  return (
    volumePrice +
    boundingBox * (priceRequest.boundingBoxPrice || 0) +
    surfaceArea * (priceRequest.surfacePrice || 0)
  )
}

export const calculateModelMachiningPrice = (
  priceRequest: Partial<PriceRequest>,
  {volume = 0, boundingBox = 0, surfaceArea = 0}: ModelProperties
) => {
  if (
    !priceRequest.machiningCostPerHour ||
    !priceRequest.machiningMaterialRemovalRate ||
    !priceRequest.finishingMaterialRemovalRate ||
    !priceRequest.density ||
    !priceRequest.weightPrice
  ) {
    return 0
  }

  const materialCost = boundingBox * priceRequest.density * priceRequest.weightPrice
  const roughMachiningCost =
    (boundingBox - volume) *
    priceRequest.machiningMaterialRemovalRate *
    priceRequest.machiningCostPerHour *
    (1 / 60)
  const finishingCost =
    surfaceArea *
    priceRequest.finishingMaterialRemovalRate *
    priceRequest.machiningCostPerHour *
    (1 / 60)

  return materialCost + roughMachiningCost + finishingCost
}

// Note: we calculate and return rounded values for all returned prices (to be in sync with the backend)
export const calculatePrices = (
  priceRequest: Partial<PriceRequest>,
  modelPrice: number,
  quantity: number,
  pricePremiumShare: number,
  modelFee: number,
  ccMargin: number,
  profitShare: number
): {
  piecePrice: number
  quantityDiscountedPiecePrice: number
  totalPricePerFile: number
  totalPriceWithCCMarginAndModelFee: number
  totalProfitShare: number
  totalCustomerPrice: number
  totalCustomerPriceV2: number
  invoiceableAmountV2: number
} => {
  const piecePrice = Math.max(
    (priceRequest.baseUnitPrice || 0) + modelPrice,
    priceRequest.minimumUnitPrice || 0
  )

  const quantityDiscountedPiecePrice =
    Math.max(
      quantity ** (priceRequest.quantityDiscount || 0),
      priceRequest.quantityDiscountCap || 0
    ) * piecePrice

  const totalPricePerFile = roundNumber(
    Math.max(
      (priceRequest.basePrice || 0) + quantity * quantityDiscountedPiecePrice,
      priceRequest.minimumPrice || 0
    )
  )

  // V1 pricing: Total price per file + production premium (30%)
  const totalCustomerPrice = roundNumber(totalPricePerFile * (1 + pricePremiumShare))

  // V2 pricing: (Total price per file + 25% CC Margin + 1.2 USD per file) + 30% profit share
  const numberOfFiles = 1 // fixed for now - does not make sense to make this dynamic for the user
  const ccMarginFee = roundNumber(totalPricePerFile * ccMargin)
  const totalPriceWithCCMargin = roundNumber(totalPricePerFile + ccMarginFee)
  const ccModelFee = roundNumber(numberOfFiles * modelFee)

  const totalPriceWithCCMarginAndModelFee =
    totalPricePerFile > 0 ? roundNumber(totalPriceWithCCMargin + ccModelFee) : 0

  const totalProfitShare = roundNumber(totalPriceWithCCMarginAndModelFee * profitShare)

  const totalCustomerPriceV2 = roundNumber(totalPriceWithCCMarginAndModelFee + totalProfitShare)

  const invoiceableAmountV2 = roundNumber(totalPricePerFile + totalProfitShare / 2)

  return {
    piecePrice,
    quantityDiscountedPiecePrice,
    totalPricePerFile,
    totalPriceWithCCMarginAndModelFee,
    totalProfitShare,
    totalCustomerPrice,
    totalCustomerPriceV2,
    invoiceableAmountV2,
  }
}

export const calculatePricesForShipping = (
  rawShippingPrice: number,
  ccMargin: number,
  profitShare: number
) => {
  if (rawShippingPrice <= 0) {
    return {
      totalCustomerPriceV2: 0,
      invoiceableAmountV2: 0,
    }
  }
  const priceIncludingCCMargin = roundNumber(rawShippingPrice * (1 + ccMargin))
  const totalProfitShare = roundNumber(priceIncludingCCMargin * profitShare)

  const totalCustomerPriceV2 = roundNumber(priceIncludingCCMargin + totalProfitShare)

  const invoiceableAmountV2 = roundNumber(rawShippingPrice + totalProfitShare / 2)

  return {
    totalCustomerPriceV2,
    invoiceableAmountV2,
  }
}

export const calculatePricesForAdditionalCost = (additionalCostPrice: number, ccMargin: number) => {
  if (additionalCostPrice <= 0) {
    return {
      totalCustomerPriceV2: 0,
      invoiceableAmountV2: 0,
    }
  }

  const ccMarginPrice = roundNumber(additionalCostPrice * ccMargin)
  const totalCustomerPriceV2 = additionalCostPrice
  const invoiceableAmountV2 = roundNumber(additionalCostPrice - ccMarginPrice)

  return {
    totalCustomerPriceV2,
    invoiceableAmountV2,
  }
}
